import { FunctionComponent, useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Anchor, Question } from '../../App.styles';
import NotificationBox, { Text } from '../../components/NotificationBox';
import { ResultInfo } from '../../components/Result/Result.styles';
import { GlobalContext, updateGetNotified } from '../../contexts';
import { useMetrics } from '../../hooks';
import { AMZ_BLACK } from '../../styles/colors';
import { metricEvents, MetricProperty, TestId } from '../../utils';
import { createBrandModelPair } from '../../utils/brandModelPair';
import {
  getNotifiedVariantConfigs,
  VariantType,
} from '../shared/notCompatibleConfig';
import {
  ActionButton,
  GetNotifiedContainer,
  NotifiedDescription,
  NotifiedTitle,
  PrivacyNoticeDisclaimer,
} from './NotCompatibleAmazon.styles';

// Contains i18n keys
export interface GetNotifiedVariant {
  title: string;
  description: string;
  signInInfo: string;
  allSet: string;
  optInBtn: string;
  privacyNotice: string;
  amazonIdRequired: boolean;
}

interface GetNotifiedProps {
  brand: string;
  model: string;
  variant?: GetNotifiedVariant;
}

const GetNotified: FunctionComponent<GetNotifiedProps> = ({
  brand,
  model,
  variant = getNotifiedVariantConfigs[VariantType.NOT_COMPATIBLE],
}) => {
  const { state: global, dispatch } = useContext(GlobalContext);
  const { track } = useMetrics();
  const { t } = useTranslation('common');

  const { amazonId, getNotified } = global;

  const brandModelPair = createBrandModelPair(brand, model);
  const optedIn = getNotified.has(brandModelPair);

  const createActionBtn = (onClickAction: () => void) => {
    return (
      <ActionButton
        disabled={optedIn}
        className="w-100 rounded-pill"
        type="button"
        onClick={onClickAction}
      >
        {t(variant.optInBtn)}
      </ActionButton>
    );
  };

  return (
    <GetNotifiedContainer
      data-testid={TestId.GetNotified}
      className="get-notified border"
    >
      <NotifiedTitle as={Question}>{t(variant.title)}</NotifiedTitle>

      <NotifiedDescription as={ResultInfo}>
        {t(variant.description)}
      </NotifiedDescription>

      {variant.amazonIdRequired ? (
        <>
          {!amazonId && (
            <NotificationBox variant="info">
              <Text color={AMZ_BLACK}>{t(variant.signInInfo)}</Text>
            </NotificationBox>
          )}

          {amazonId && (
            <>
              {optedIn && (
                <NotificationBox variant="success">
                  <Text>{t(variant.allSet)}</Text>
                </NotificationBox>
              )}

              {createActionBtn(() => {
                dispatch(
                  updateGetNotified({
                    getNotified: {
                      model,
                      brand,
                    },
                  })
                );
                track(metricEvents.getNotifiedOptedIn, {
                  [MetricProperty.getNotifiedOptedIn]: true,
                  brand,
                  model,
                  amazonId,
                });
              })}
            </>
          )}
        </>
      ) : (
        <>
          {createActionBtn(() => {
            track(metricEvents.betaSupportOptedIn, {
              [MetricProperty.betaSupportOptedIn]: true,
              brand,
              model,
              amazonId,
            });

            window.open(
              `${t('results.homeUrl')}${t(
                'results.notCompatible.getNotified.betaSupport.csSupportArticlePath'
              )}`,
              '_blank',
              'noopener,noreferrer'
            );
          })}
        </>
      )}

      <PrivacyNoticeDisclaimer>
        <Trans i18nKey={`common:${variant.privacyNotice}`}>
          notice{' '}
          <Anchor
            noUnderline
            href="/gp/help/customer/display.html?nodeId=GX7NJQ4ZB8MHFRNJ"
          >
            link
          </Anchor>
        </Trans>
      </PrivacyNoticeDisclaimer>
    </GetNotifiedContainer>
  );
};

export default GetNotified;
